import {
  ArchiveIcon,
  AtSignIcon,
  BookmarkIcon,
  BotIcon,
  CalendarIcon,
  CloudOffIcon,
  FilePenLineIcon,
  FileQuestionIcon,
  FileTextIcon,
  FolderGit2Icon,
  GitPullRequestIcon,
  HashIcon,
  HistoryIcon,
  InboxIcon,
  KeyRoundIcon,
  MessageSquareIcon,
  MessageSquareTextIcon,
  MessagesSquareIcon,
  MilestoneIcon,
  RefreshCwIcon,
  SearchIcon,
  SearchXIcon,
  ShapesIcon,
  ShieldIcon,
  SmileIcon,
  SquareKanbanIcon,
  TagIcon,
  UsersIcon,
  WifiOffIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/primitives/empty.tsx"
import { ButtonLink } from "./button-link.tsx"
import { filteredTitleOf, isFiltered, type Listing } from "./empty-state.ts"

/*
 * The words for a list that holds nothing. They live here rather than at the call site, as the
 * destructive confirmation's do, so every empty list explains itself alike and a page cannot say a
 * generic "Nothing here". A page listing something new adds its case here, and in `Listing`.
 */

export { countOf, tableListingOf, type Listing, type TableListing } from "./empty-state.ts"

/**
 * An empty list whose words name something: the group nobody is in, the address nothing is at, the
 * name no channel holds (a search alone narrows Chat's directory, so it says no filter).
 */
export type NamedEmptiness =
  | { listing: "group-empty"; group: string }
  | { listing: "not-found"; path: string }
  | { listing: "chat-channels-searched"; query: string }
  | { listing: "chat-messages-searched"; query: string }

type Wording = { Icon: LucideIcon; title: string; description?: string }

const nothingYet: Record<Exclude<Listing, `${string}-filtered`>, Wording> = {
  members: {
    Icon: UsersIcon,
    title: "No Members yet",
    description: "Everyone in the company's Google Workspace and Slack is listed once brought in.",
  },
  "agent-models": {
    Icon: BotIcon,
    title: "No answers in this range",
    description: "Each model, Effort and side appears once it has answered in the range.",
  },
  runs: {
    Icon: MessageSquareTextIcon,
    title: "No questions answered that day",
    description:
      "Runs answer questions asked in Slack. What change requests and embeddings cost shows on the chart.",
  },
  "members-by-spend": {
    Icon: UsersIcon,
    title: "No Member asked that day",
    description: "The Members whose runs cost most appear once someone has asked Beacon something.",
  },
  "specimen-members": { Icon: UsersIcon, title: "No Members yet" },
  "specimen-models": { Icon: BotIcon, title: "No answers in this range" },
  "change-requests": {
    Icon: FilePenLineIcon,
    title: "No change requests yet",
    description:
      "Start one with an idea or a bug: the agent reads the code and the tickets, then asks what it needs to know.",
  },
  projects: {
    Icon: SquareKanbanIcon,
    title: "No Projects yet",
    description: "Create one, or ask a colleague to give you access to theirs.",
  },
  documents: {
    Icon: FileTextIcon,
    title: "No Documents yet",
    description: "Write one in markdown beside its pages as they'll print.",
  },
  "documents-read-only": {
    Icon: FileTextIcon,
    title: "No Documents yet",
    description: "Documents put here by those who can write in it show here.",
  },
  "document-releases": {
    Icon: MilestoneIcon,
    title: "No Versions yet",
    description:
      "Publishing freezes the Draft as v1.0, its first Version, with a PDF that never changes.",
  },
  "my-tickets": {
    Icon: InboxIcon,
    title: "Nothing on your plate",
    description: "Nothing assigned to you is still open.",
  },
  "my-tickets-all": { Icon: InboxIcon, title: "Nothing is assigned to you." },
  "board-new": {
    Icon: SquareKanbanIcon,
    title: "Create the first ticket",
    description: "It lands in the first column; move it right as the work moves.",
  },
  epics: {
    Icon: ZapIcon,
    title: "No Epics yet",
    description: "An Epic groups the tickets that make up one piece of work.",
  },
  "epic-tickets": {
    Icon: ZapIcon,
    title: "No tickets in this Epic yet",
    description: "Put one in from its own page.",
  },
  "chat-channels": {
    Icon: HashIcon,
    title: "No channels yet",
    description: "Start one for a team, a topic or a piece of work; everyone can find and read it.",
  },
  "chat-threads": {
    Icon: MessageSquareTextIcon,
    title: "No threads yet",
    description:
      "Reply in a thread, or follow one, and its new replies gather here, in every conversation.",
  },
  "chat-mentions": {
    Icon: AtSignIcon,
    title: "No mentions yet",
    description:
      "When someone mentions you, or a group you're in, where you can read it, it gathers here.",
  },
  "chat-inbox": {
    Icon: InboxIcon,
    title: "Nothing from Beacon yet",
    description:
      "When someone assigns you work, mentions you in a ticket or comments on one assigned to you, or your change request has something for you, Beacon tells you here.",
  },
  "chat-messages": {
    Icon: MessagesSquareIcon,
    title: "No messages yet",
    description: "Everything said here stays, for everyone at the company to read.",
  },
  "chat-search": {
    Icon: SearchIcon,
    title: "Search messages",
    description:
      "Find what was said in every public channel, joined or not, and in your private channels and direct messages.",
  },
  "chat-saved": {
    Icon: BookmarkIcon,
    title: "No saved messages",
    description:
      "Save a message from its ⋯ to come back to it here. Only you see what you save, and only while you can read it.",
  },
  "documents-archived": {
    Icon: ArchiveIcon,
    title: "Nothing archived",
    description:
      "A Folder or Document archived waits here, read-only, until someone with Write restores it.",
  },
  "custom-emoji": {
    Icon: SmileIcon,
    title: "No custom emoji yet",
    description:
      "Add the first with Add emoji, here or from an emoji picker's Custom tab. Every Member can, for the whole company to use.",
  },
  groups: {
    Icon: ShapesIcon,
    title: "No groups yet",
    description:
      "Groups are named sets of Members, such as Exec or Sales, that knowledge can be shared with.",
  },
  "slack-rules": {
    Icon: MessageSquareIcon,
    title: "Post updates to Slack",
    description: "Beacon posts a line to a channel for each event its rule chooses.",
  },
  "spend-day": { Icon: CalendarIcon, title: "Pick a day on the chart, or choose one" },
  "activity-none": { Icon: HistoryIcon, title: "No Project-level activity yet" },
  "ticket-comments": { Icon: MessageSquareIcon, title: "No comments yet" },
  "slack-user-groups": { Icon: UsersIcon, title: "Slack has no user groups to offer." },
  "github-installations": {
    Icon: GitPullRequestIcon,
    title: "Beacon isn't installed on GitHub yet",
    description:
      "Install it on your organisation, choosing the repositories it may read. It appears here once GitHub says so.",
  },
  "linked-repos": {
    Icon: FolderGit2Icon,
    title: "No repositories linked yet",
    description:
      "Link the repositories this Project is built in, so their pull requests find its tickets.",
  },
  "connected-agents": {
    Icon: BotIcon,
    title: "No agents connected yet",
    description: "Add Beacon to one of your agents above. It shows here once you allow it.",
  },
  "access-tokens": {
    Icon: KeyRoundIcon,
    title: "No access tokens yet",
    description: "Create one for a script that calls Beacon's tools without signing in.",
  },
  areas: {
    Icon: ShapesIcon,
    title: "No Areas yet",
    description: "Areas group a Project's tickets by the line of work they belong to.",
  },
  labels: {
    Icon: TagIcon,
    title: "No Labels yet",
    description: "Labels mark a Project's tickets and Epics, any number on each.",
  },
}

function wordingOf(of: Listing | NamedEmptiness): Wording & { path?: string } {
  if (typeof of === "object")
    switch (of.listing) {
      case "group-empty":
        return { Icon: UsersIcon, title: `Nobody is in ${of.group} yet` }
      case "not-found":
        return { Icon: FileQuestionIcon, title: "Nothing at this address", path: of.path }
      case "chat-channels-searched":
        return {
          Icon: SearchXIcon,
          title: `No channels named “${of.query}”`,
          description: "Try another name, or clear the search.",
        }
      case "chat-messages-searched":
        return {
          Icon: SearchXIcon,
          title: of.query ? `No messages match “${of.query}”` : "No messages match these filters",
          description:
            "Try other words, or remove a filter. Only what you can read now is searched.",
        }
    }
  if (isFiltered(of))
    return {
      Icon: SearchXIcon,
      title: filteredTitleOf(of),
      description: "Remove a filter, or clear them all.",
    }
  return nothingYet[of]
}

/**
 * The state of a list with nothing in it: an icon on a sunken tile, a title, a line saying why or
 * what to do, and the page's `actions` under them — "Clear filters" on every filtered case, which
 * the page passes, since the page owns its filters. `framed` is the tighter fit inside a panel or a
 * Board column.
 */
export function EmptyState({
  of,
  actions,
  framed = false,
}: {
  of: Listing | NamedEmptiness
  actions?: ReactNode
  framed?: boolean
}) {
  const { Icon, title, description, path } = wordingOf(of)
  return (
    <Empty className={framed ? "py-8" : undefined}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
        {path !== undefined && (
          <EmptyDescription>
            <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-xs break-all text-foreground">
              {path}
            </code>
          </EmptyDescription>
        )}
      </EmptyHeader>
      {actions && <EmptyContent>{actions}</EmptyContent>}
    </Empty>
  )
}

/**
 * Why a whole page can't be shown. `refusal` is the web API's code, as `refusalOf` reads it off the
 * failed query, or one of the shell's own two: `updated` and `offline`.
 *
 * - An Admin's page refused to a Member says "Admins only", and offers the way home.
 * - What the page shows being missing, or out of the Member's reach, says `missing`: the page's own
 *   sentence, such as "Project not found" or "VRF-9 doesn't exist, or you can't see it", and offers
 *   `back`.
 * - A page whose code a new release replaced (`updated`) says Beacon has been updated, and offers
 *   `onRetry` as Reload, which takes the release.
 * - The shell opened with no network (`offline`) says so: it carries on by itself once the network
 *   is back. `onRetry` is Try again, for a browser that says it is offline when it isn't.
 * - Anything else, a network failure included, says something went wrong and offers `onRetry`,
 *   which is the query's `refetch`.
 */
export function RefusalState({
  refusal,
  missing = "That doesn't exist, or you can't see it",
  back,
  onRetry,
}: {
  refusal: string
  missing?: string
  back?: { label: string; href: string }
  onRetry?: () => void
}) {
  const shown: { Icon: LucideIcon; title: string; description?: string; action?: ReactNode } =
    refusal === "not-an-admin"
      ? {
          Icon: ShieldIcon,
          title: "Admins only",
          description: "Ask an Admin if you need something here.",
          action: <LinkButton label="Go home" href="/" />,
        }
      : refusal === "not-found" || refusal === "ticket-not-found"
        ? {
            Icon: FileQuestionIcon,
            title: missing,
            ...(back && { action: <LinkButton label={back.label} href={back.href} /> }),
          }
        : refusal === "updated"
          ? {
              Icon: RefreshCwIcon,
              title: "Beacon has been updated",
              description: "Reload to open this page in the new version.",
              ...(onRetry && { action: <RetryButton label="Reload" onRetry={onRetry} /> }),
            }
          : refusal === "offline"
            ? {
                Icon: WifiOffIcon,
                title: "You're offline",
                description: "Beacon will pick up where you left off when you're back online.",
                ...(onRetry && { action: <RetryButton label="Try again" onRetry={onRetry} /> }),
              }
            : {
                Icon: CloudOffIcon,
                title: "Something went wrong",
                description:
                  "Beacon couldn't load this page. Check your connection, then try again.",
                ...(onRetry && { action: <RetryButton label="Try again" onRetry={onRetry} /> }),
              }
  return (
    <Empty role="alert">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <shown.Icon />
        </EmptyMedia>
        <EmptyTitle>{shown.title}</EmptyTitle>
        {shown.description && <EmptyDescription>{shown.description}</EmptyDescription>}
      </EmptyHeader>
      {shown.action && <EmptyContent>{shown.action}</EmptyContent>}
    </Empty>
  )
}

function RetryButton({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <Button variant="outline" size="sm" onClick={() => onRetry()}>
      {label}
    </Button>
  )
}

function LinkButton({ label, href }: { label: string; href: string }) {
  return (
    <ButtonLink to={href} variant="outline" size="sm">
      {label}
    </ButtonLink>
  )
}
